// One search over stations and the stops of every mode a phone has, for the places that ask "where".
//
// IMPORTS ONLY `modes.js`, which imports nothing. It is handed the result lists and the words typed, and hands back
// one list; reading the bundled network and the tram bundle is the caller's.
// That keeps this file out of the first load (the tram bundle is lazy) and
// lets the ranking be checked without a browser.
//
// A TRAM STOP IS A ROW IN THE SAME LIST, with a mode on it. The two kinds of
// answer are told apart by a badge, not by two lists on screen: somebody typing
// "Flinders" wants Flinders Street and every other thing called that, and a
// second heading would make them read twice to find the one they meant.
//
// RANKED BY HOW WELL THE NAME ANSWERS WHAT WAS TYPED, then trains before trams
// at a tie — this is a train app first, and "Richmond" is the station — then
// each list's own order. A search that is plainly about a tram stop (a stop
// number, "stop 6", "#6") has no station to compete with and is all trams.
//
// NO LIST CAN CROWD THE OTHER OUT. Ten answers is a screen; if a short name
// fills it with stations, the tram stop on Swan St that matched as well is
// under the fold and the person types more to find it. Up to `minEach` of each
// kind are always kept.

import { modeOfRow, realStopNumber } from './modes.js';

/** A row's mode id: stations are trains, everything else says what it is. */
export const modeOf = (row) => (row?.mode && row.mode !== 'train' ? String(row.mode) : 'train');

/** The key a row is unique by: another mode's stop id can equal a station's. */
export const rowKey = (row) => `${modeOf(row)}:${Number(row?.stopId)}`;

/** A mode's stop as the pickers carry it, from `stopInfo` in mode-stops.js. */
export function modeRow(mode, info) {
  if (!info) return null;
  return {
    mode: mode.id,
    stopId: info.stopId,
    name: info.display || info.name,
    number: info.number,
    suburb: info.suburb ?? '',
    street: info.name,
    trainStopId: info.trainStopId ?? null,
  };
}

/**
 * The small line under a row's name: where it is, and for a stop of a mode its
 * number — "STOP 6 · MELBOURNE CBD" is what the pole and the tram's own screen
 * both say.
 */
export function rowNote(row) {
  const suburb = String(row?.suburb ?? '').trim();
  const mode = modeOf(row) === 'train' ? null : modeOfRow({ mode: modeOf(row) });
  if (mode) {
    // A stop known by a landmark ("Bourke Street Mall") says which poles it is.
    const street = String(row.street ?? '').trim();
    const named = String(row.name ?? '').trim();
    const place = street && street.toLowerCase() !== named.toLowerCase()
      ? street
      : (suburb && suburb.toLowerCase() !== named.toLowerCase() ? suburb : '');
    const number = realStopNumber(row.number);
    const stop = number ? `${mode.search.noteNumber} ${number}` : mode.search.noteFallback;
    return place ? `${stop} · ${place.toUpperCase()}` : stop;
  }
  return suburb.toLowerCase() === String(row?.name ?? '').trim().toLowerCase() ? '' : suburb;
}

const tier = (text, q) => {
  const t = String(text ?? '').toLowerCase();
  if (!t || !q) return 4;
  if (t === q) return 0;
  // Whole first word ("Swan St/Church St" for "swan") before a longer one
  // ("Swanston St/…"): the street somebody named beats one that merely starts
  // the same.
  if (t.startsWith(q)) return /[a-z0-9]/.test(t[q.length] ?? ' ') ? 1.5 : 1;
  if (t.split(/[^a-z0-9]+/).some((w) => w.startsWith(q))) return 2;
  return 3;
};

/**
 * Merge the answers.
 *
 * @param {object} input
 * @param {string} input.term     what was typed
 * @param {Array}  input.stations station records, best first
 * @param {object} [input.modes]  `{ [modeId]: rows }`, each list best first
 * @param {number} [input.limit]  rows to return
 * @param {number} [input.minEach] rows of each kind kept when there are that many
 */
export function mergeStops({ term = '', stations = [], modes = {}, limit = 10, minEach = 3 } = {}) {
  const q = String(term).trim().toLowerCase();
  const kinds = Object.keys(modes);
  const all = [
    ...stations.map((row, i) => ({ row, mode: 0, i, tier: tier(row.name, q) })),
    ...kinds.flatMap((id, k) => (modes[id] ?? []).map((row, i) => ({
      row, mode: k + 1, i, tier: Math.min(tier(row.name, q), tier(row.street, q)),
    }))),
  ];
  all.sort((a, b) => a.tier - b.tier || a.mode - b.mode || a.i - b.i);
  if (all.length <= limit) return all.map((x) => x.row);

  const keep = new Set();
  for (let mode = 0; mode <= kinds.length; mode += 1) {
    all.filter((x) => x.mode === mode).slice(0, minEach).forEach((x) => keep.add(x));
  }
  for (const x of all) {
    if (keep.size >= limit) break;
    keep.add(x);
  }
  return all.filter((x) => keep.has(x)).map((x) => x.row);
}
